import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  TrendingUp,
  Plus,
  Trash2,
  Calculator,
  Info,
  DollarSign,
  Users,
  PieChart,
  AlertTriangle,
  BarChart3,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { calcExitWaterfall } from '@/utils/equityMath';

interface FundingRound {
  id: string;
  name: string;
  investmentAmount: number;
  postMoneyValuation: number;
  liquidationPreference: number; // 1x, 2x, etc.
  participating: boolean;
  participationCap: number | null; // null = uncapped
  shares: number;
}

interface ExitScenario {
  name: string;
  exitValue: number;
  color: string;
}

interface WaterfallResult {
  roundName: string;
  liquidationPref: number;
  participating: number;
  common: number;
  total: number;
  roi: number;
  multiple: number;
}

const COLORS = {
  seed: '#8b5cf6',
  seriesA: '#3b82f6',
  seriesB: '#10b981',
  seriesC: '#f59e0b',
  common: '#6b7280',
};

export function ExitWaterfallCalculator() {
  const [rounds, setRounds] = useState<FundingRound[]>([
    {
      id: '1',
      name: 'Seed',
      investmentAmount: 1000000,
      postMoneyValuation: 5000000,
      liquidationPreference: 1,
      participating: false,
      participationCap: null,
      shares: 1000000,
    },
    {
      id: '2',
      name: 'Series A',
      investmentAmount: 5000000,
      postMoneyValuation: 20000000,
      liquidationPreference: 1,
      participating: true,
      participationCap: 3,
      shares: 1250000,
    },
  ]);

  const [commonShares, setCommonShares] = useState(7500000);
  const [exitScenarios, setExitScenarios] = useState<ExitScenario[]>([
    { name: 'Down Exit', exitValue: 10000000, color: '#ef4444' },
    { name: '1x Exit', exitValue: 20000000, color: '#f59e0b' },
    { name: '3x Exit', exitValue: 60000000, color: '#10b981' },
    { name: '5x Exit', exitValue: 100000000, color: '#3b82f6' },
  ]);

  const [selectedScenario, setSelectedScenario] = useState(0);

  const addRound = () => {
    const newRound: FundingRound = {
      id: Date.now().toString(),
      name: `Series ${String.fromCharCode(65 + rounds.length - 1)}`,
      investmentAmount: 0,
      postMoneyValuation: 0,
      liquidationPreference: 1,
      participating: false,
      participationCap: null,
      shares: 0,
    };
    setRounds([...rounds, newRound]);
  };

  const removeRound = (id: string) => {
    setRounds(rounds.filter((r) => r.id !== id));
  };

  const updateRound = (id: string, field: keyof FundingRound, value: any) => {
    setRounds(rounds.map((r) => (r.id === id ? { ...r, [field]: value } : r)));
  };

  // The maths lives in calcExitWaterfall so it can be tested; this only appends the
  // common row the table and charts below expect. It previously carried its own copy
  // of the algorithm, which paid the residual out twice and could distribute more
  // than the exit value.
  const calculateWaterfall = (exitValue: number): WaterfallResult[] => {
    const { results, commonPayout } = calcExitWaterfall(rounds, commonShares, exitValue);

    return [
      ...results,
      {
        roundName: 'Common',
        liquidationPref: 0,
        participating: 0,
        common: commonPayout,
        total: commonPayout,
        roi: commonPayout > 0 ? 0 : -100,
        multiple: 0,
      },
    ];
  };

  const currentResults = calculateWaterfall(exitScenarios[selectedScenario].exitValue);
  const totalInvested = rounds.reduce((sum, r) => sum + r.investmentAmount, 0);
  const totalPayout = currentResults.reduce((sum, r) => sum + r.total, 0);

  const chartData = currentResults.map((r) => ({
    name: r.roundName,
    'Liquidation Pref': r.liquidationPref,
    Participating: r.participating,
    Common: r.common,
    total: r.total,
  }));

  const scenarioComparisonData = exitScenarios.map((scenario) => {
    const results = calculateWaterfall(scenario.exitValue);
    const data: any = { scenario: scenario.name, exitValue: scenario.exitValue };

    results.forEach((r) => {
      data[r.roundName] = r.total;
    });

    return data;
  });

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Exit Waterfall Calculator
          </CardTitle>
          <CardDescription>
            Model distribution of exit proceeds across funding rounds with liquidation preferences
            and participation rights
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <Tabs defaultValue="structure" className="w-full">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="structure">Cap Table Structure</TabsTrigger>
              <TabsTrigger value="scenarios">Exit Scenarios</TabsTrigger>
              <TabsTrigger value="results">Waterfall Results</TabsTrigger>
            </TabsList>

            <TabsContent value="structure" className="space-y-4 mt-4">
              {/* Funding Rounds */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <Label className="text-base font-semibold">Funding Rounds</Label>
                  <Button onClick={addRound} size="sm" variant="outline">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Round
                  </Button>
                </div>

                {rounds.map((round, idx) => (
                  <Card key={round.id} className="border-muted">
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between">
                        <CardTitle className="text-sm">{round.name}</CardTitle>
                        <Button
                          onClick={() => removeRound(round.id)}
                          size="sm"
                          variant="ghost"
                          disabled={rounds.length === 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Round Name</Label>
                          <Input
                            value={round.name}
                            onChange={(e) => updateRound(round.id, 'name', e.target.value)}
                            placeholder="Series A"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Investment Amount</Label>
                          <Input
                            type="number"
                            value={round.investmentAmount}
                            onChange={(e) =>
                              updateRound(round.id, 'investmentAmount', Number(e.target.value))
                            }
                            placeholder="5000000"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Post-Money Valuation</Label>
                          <Input
                            type="number"
                            value={round.postMoneyValuation}
                            onChange={(e) =>
                              updateRound(round.id, 'postMoneyValuation', Number(e.target.value))
                            }
                            placeholder="20000000"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Shares Issued</Label>
                          <Input
                            type="number"
                            value={round.shares}
                            onChange={(e) =>
                              updateRound(round.id, 'shares', Number(e.target.value))
                            }
                            placeholder="1250000"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Liquidation Preference</Label>
                          <Select
                            value={round.liquidationPreference.toString()}
                            onValueChange={(v) =>
                              updateRound(round.id, 'liquidationPreference', Number(v))
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="1">1x (Standard)</SelectItem>
                              <SelectItem value="1.5">1.5x</SelectItem>
                              <SelectItem value="2">2x (Senior)</SelectItem>
                              <SelectItem value="3">3x (Super Senior)</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label className="text-xs">Participation</Label>
                          <Select
                            value={round.participating ? 'participating' : 'non-participating'}
                            onValueChange={(v) =>
                              updateRound(round.id, 'participating', v === 'participating')
                            }
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="non-participating">Non-Participating</SelectItem>
                              <SelectItem value="participating">Participating</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        {round.participating && (
                          <div className="col-span-2">
                            <Label className="text-xs">
                              Participation Cap (multiple of investment)
                            </Label>
                            <Input
                              type="number"
                              value={round.participationCap || ''}
                              onChange={(e) =>
                                updateRound(
                                  round.id,
                                  'participationCap',
                                  e.target.value ? Number(e.target.value) : null
                                )
                              }
                              placeholder="3 (or leave empty for uncapped)"
                            />
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <Badge variant="secondary" className="text-xs">
                          {round.liquidationPreference}x Preference
                        </Badge>
                        <Badge
                          variant={round.participating ? 'default' : 'outline'}
                          className="text-xs"
                        >
                          {round.participating ? 'Participating' : 'Non-Participating'}
                        </Badge>
                        {round.participating && round.participationCap && (
                          <Badge variant="secondary" className="text-xs">
                            {round.participationCap}x Cap
                          </Badge>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Common Stock */}
                <Card className="border-muted">
                  <CardHeader className="pb-3">
                    <CardTitle className="text-sm">Common Stock</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div>
                      <Label className="text-xs">Common Shares Outstanding</Label>
                      <Input
                        type="number"
                        value={commonShares}
                        onChange={(e) => setCommonShares(Number(e.target.value))}
                        placeholder="7500000"
                      />
                    </div>
                  </CardContent>
                </Card>
              </div>

              <Alert>
                <Info className="h-4 w-4" />
                <AlertDescription className="text-sm">
                  <strong>Liquidation Preference:</strong> Investors receive their preference amount
                  before common shareholders.
                  <strong className="ml-2">Participating Preferred:</strong> After receiving
                  preference, investors also participate pro-rata in remaining proceeds.
                </AlertDescription>
              </Alert>
            </TabsContent>

            <TabsContent value="scenarios" className="space-y-4 mt-4">
              <div className="grid md:grid-cols-2 gap-4">
                {exitScenarios.map((scenario, idx) => (
                  <Card
                    key={idx}
                    className={`border-2 cursor-pointer transition-all ${selectedScenario === idx ? 'border-primary' : 'border-muted'}`}
                    onClick={() => setSelectedScenario(idx)}
                  >
                    <CardHeader>
                      <CardTitle className="text-sm flex items-center justify-between">
                        {scenario.name}
                        {selectedScenario === idx && <Badge>Selected</Badge>}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        <div>
                          <Label className="text-xs">Exit Value</Label>
                          <Input
                            type="number"
                            value={scenario.exitValue}
                            onChange={(e) => {
                              const newScenarios = [...exitScenarios];
                              newScenarios[idx].exitValue = Number(e.target.value);
                              setExitScenarios(newScenarios);
                            }}
                            onClick={(e) => e.stopPropagation()}
                          />
                        </div>
                        <div className="text-sm text-muted-foreground">
                          ${(scenario.exitValue / 1000000).toFixed(1)}M exit
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* Scenario Comparison Chart */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Multi-Scenario Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={scenarioComparisonData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="scenario" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`}
                      />
                      <Legend />
                      {rounds.map((round, idx) => (
                        <Bar
                          key={round.id}
                          dataKey={round.name}
                          stackId="a"
                          fill={Object.values(COLORS)[idx]}
                        />
                      ))}
                      <Bar dataKey="Common" stackId="a" fill={COLORS.common} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="results" className="space-y-4 mt-4">
              {/* Summary Cards */}
              <div className="grid md:grid-cols-4 gap-4">
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground">Exit Value</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      ${(exitScenarios[selectedScenario].exitValue / 1000000).toFixed(1)}M
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground">Total Invested</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">${(totalInvested / 1000000).toFixed(1)}M</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground">
                      Blended Multiple
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      {(exitScenarios[selectedScenario].exitValue / totalInvested).toFixed(2)}x
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-xs text-muted-foreground">Common Gets</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold">
                      $
                      {(
                        (currentResults.find((r) => r.roundName === 'Common')?.total || 0) / 1000000
                      ).toFixed(1)}
                      M
                    </p>
                  </CardContent>
                </Card>
              </div>

              {/* Waterfall Visualization */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <BarChart3 className="h-4 w-4" />
                    Distribution Waterfall - {exitScenarios[selectedScenario].name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" />
                      <YAxis />
                      <RechartsTooltip
                        formatter={(value: number) => `$${(value / 1000000).toFixed(2)}M`}
                      />
                      <Legend />
                      <Bar dataKey="Liquidation Pref" stackId="a" fill="#8b5cf6" />
                      <Bar dataKey="Participating" stackId="a" fill="#3b82f6" />
                      <Bar dataKey="Common" stackId="a" fill="#10b981" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Detailed Results Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm">Detailed Payout Breakdown</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left p-2 text-sm font-semibold">Round</th>
                          <th className="text-right p-2 text-sm font-semibold">Liquidation Pref</th>
                          <th className="text-right p-2 text-sm font-semibold">Participating</th>
                          <th className="text-right p-2 text-sm font-semibold">Common</th>
                          <th className="text-right p-2 text-sm font-semibold">Total Payout</th>
                          <th className="text-right p-2 text-sm font-semibold">Multiple</th>
                          <th className="text-right p-2 text-sm font-semibold">ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentResults.map((result, idx) => (
                          <tr key={idx} className="border-b">
                            <td className="p-2 font-medium">{result.roundName}</td>
                            <td className="text-right p-2">
                              ${(result.liquidationPref / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2">
                              ${(result.participating / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2">
                              ${(result.common / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2 font-semibold">
                              ${(result.total / 1000000).toFixed(2)}M
                            </td>
                            <td className="text-right p-2">
                              {result.roundName !== 'Common'
                                ? `${result.multiple.toFixed(2)}x`
                                : '—'}
                            </td>
                            <td
                              className={`text-right p-2 font-medium ${result.roi >= 0 ? 'text-success' : 'text-destructive'}`}
                            >
                              {result.roundName !== 'Common'
                                ? `${result.roi >= 0 ? '+' : ''}${result.roi.toFixed(1)}%`
                                : '—'}
                            </td>
                          </tr>
                        ))}
                        <tr className="font-bold bg-muted/50">
                          <td className="p-2">Total</td>
                          <td className="text-right p-2">
                            $
                            {(
                              currentResults.reduce((s, r) => s + r.liquidationPref, 0) / 1000000
                            ).toFixed(2)}
                            M
                          </td>
                          <td className="text-right p-2">
                            $
                            {(
                              currentResults.reduce((s, r) => s + r.participating, 0) / 1000000
                            ).toFixed(2)}
                            M
                          </td>
                          <td className="text-right p-2">
                            $
                            {(currentResults.reduce((s, r) => s + r.common, 0) / 1000000).toFixed(
                              2
                            )}
                            M
                          </td>
                          <td className="text-right p-2">${(totalPayout / 1000000).toFixed(2)}M</td>
                          <td className="text-right p-2">—</td>
                          <td className="text-right p-2">—</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>

              {/* Insights */}
              {currentResults[currentResults.length - 1].total === 0 && (
                <Alert variant="destructive">
                  <AlertTriangle className="h-4 w-4" />
                  <AlertDescription>
                    <strong>Warning:</strong> In this exit scenario, common shareholders receive
                    nothing. All proceeds go to preferred shareholders to satisfy liquidation
                    preferences.
                  </AlertDescription>
                </Alert>
              )}
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
